import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid, RadioGroup, Tooltip, Typography } from '@mui/material';
import { Help } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { number, object, date, string, boolean } from 'yup';
import { type LanguageQualificationFormValues, type FormikProp } from './types';
import { languageQualificationTestTypeOptions } from './options';
import { editLanguagePermission } from './editLanguagePermission';
import { PAGE_TEXT } from '../lang';
import {
  APPLICATION_MODULE,
  LANGUAGE_QUALIFICATION_TEST_TYPE,
} from '../constants';
import { APPLICATION_STATUS_MODULE } from '../../ApplicationStatus/constants';
import {
  type LanguageQualificationFormData,
  type LanguageResult,
  addApplicationItem,
  useUpdateLanguageQualification,
} from '../../../graphql';
import {
  type AlertDetails,
  Alert,
  Button,
  TextField,
  Select,
  DatePicker,
  Radio,
  IconButton,
  FilesPanel,
  type FileData,
  FILE_MANAGE_ORDER,
  Dialog,
} from '../../../../../components';
import {
  handleReadySubmit,
  handleFileRename,
  type FormController,
  FormModificationMode,
} from '../../../../../utils';

type LanguageQualificationFormProps = {
  applicationNo: string;
  initialValues: LanguageQualificationFormData;
  enableEdit: FormController;
  enableDelete: boolean;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

// IELTS: listening/reading/writing/speaking/overall
// TOEFL: reading/listening/speaking/writing/overall
export const languageResultOrderMap: Record<
  LANGUAGE_QUALIFICATION_TEST_TYPE,
  Array<keyof LanguageResult>
> = {
  [LANGUAGE_QUALIFICATION_TEST_TYPE.IELTS]: [
    'listening',
    'reading',
    'writing',
    'speaking',
    'overall',
  ],
  [LANGUAGE_QUALIFICATION_TEST_TYPE.TOEFL]: [
    'reading',
    'listening',
    'speaking',
    'writing',
    'overall',
  ],
};

const {
  QUALIFICATION: { LANGUAGE_QUALIFICATION },
} = PAGE_TEXT;

export const languageResultslabelMap: Record<string, string> = {
  listening: LANGUAGE_QUALIFICATION.LISTENING,
  reading: LANGUAGE_QUALIFICATION.READING,
  writing: LANGUAGE_QUALIFICATION.WRITING,
  speaking: LANGUAGE_QUALIFICATION.SPEAKING,
  overall: LANGUAGE_QUALIFICATION.OVERALL_BAND_SCORE,
};

export const LanguageQualificationForm: React.FunctionComponent<
  LanguageQualificationFormProps
> = props => {
  const {
    applicationNo,
    initialValues,
    enableEdit,
    refetchApplication,
    handleLoading,
    enableDelete,
  } = props;
  const { applicationId = '' } = useParams();
  const { formModificationMode } = enableEdit;
  const isConditionEdit =
    formModificationMode === FormModificationMode.CONDITION_MEET;

  const {
    supportingDocuments,
    languageQualificationId: initialLanguageQualificationId = '',
    ...languageQualificationFormValues
  } = initialValues;
  const [openDialog, setOpenDialog] = useState(false);

  const [languageQualificationId, setLanguageQualificationId] =
    useState<string>(initialLanguageQualificationId);
  const isCreateMode = !languageQualificationId;

  const [filesArray, setFilesArray] = useState<FileData[]>(
    supportingDocuments ?? [],
  );

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'info',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const createLanguageQualification = addApplicationItem(
    'languageQualifications',
    refetchApplication,
  );
  const updateLanguageQualification = useUpdateLanguageQualification();
  const handleSubmit = async (
    values: LanguageQualificationFormValues,
  ): Promise<void> => {
    const { needEnglishTest, resultAnnouncedIndicator } = values;
    if (
      (!needEnglishTest || resultAnnouncedIndicator) &&
      filesArray.length < 1
    ) {
      alertDetailDef = {
        alertContent: 'Please upload supporting documents.',
        alertType: 'warning',
      };
      setOpenAlert(true);
      setAlertDetails(alertDetailDef);
      return;
    }

    await handleLoading(async () => {
      try {
        const {
          listening,
          reading,
          speaking,
          writing,
          overall,
          expectedResultUpdateDate,
          resultAnnouncedIndicator,
          testType,
          testDate,
          resultReceiptDate,
          needEnglishTest,
          referenceNo,
        } = values;

        const inputSupportingDocuments = filesArray.map((doc, index) => {
          const { name, ...other } = doc;
          return {
            name: handleFileRename(applicationNo, 'language', index + 1),
            ...other,
          };
        });

        const isValidValue = (value: any): boolean =>
          value !== null && value !== undefined && value !== '';

        // if don't have "expectedResultUpdateDate" value, don't pass to backend graphql
        // otherwise will cause Date type error
        const languageQualificationInput = {
          needEnglishTest,
          resultAnnouncedIndicator,
          testType: isValidValue(testType) ? testType : null,
          testDate: isValidValue(testDate) ? testDate : null,
          result:
            needEnglishTest && resultAnnouncedIndicator
              ? {
                  listening,
                  reading,
                  speaking,
                  writing,
                  overall,
                  resultReceiptDate,
                }
              : null,
          referenceNo: isValidValue(referenceNo) ? referenceNo : null,
          expectedResultUpdateDate: isValidValue(expectedResultUpdateDate)
            ? expectedResultUpdateDate
            : null,
          supportingDocuments: inputSupportingDocuments,
        };

        if (isCreateMode) {
          const res = await createLanguageQualification({
            variables: {
              id: applicationId,
              input: { languageQualification: languageQualificationInput },
            },
          });
          const resLanguageQualificationId =
            res.data?.addApplicationItem.languageQualifications[0]._id;
          setLanguageQualificationId(resLanguageQualificationId);
        } else {
          const variables = {
            id: languageQualificationId,
            input: languageQualificationInput,
          };
          await updateLanguageQualification({ variables });
        }
        alertDetailDef = {
          alertContent: isConditionEdit // if update through the condition meet
            ? PAGE_TEXT.CONDITION_SAVE_ALTER_TEXT
            : 'Saved Language Qualification Successfully.',
          alertType: 'success',
        };
        refetchApplication();
      } catch (error) {
        alertDetailDef = {
          alertContent:
            'Saved Language Qualification Failed.' + (error as Error).message,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }
    });

    setOpenAlert(true);
  };

  const formik: FormikProp = useFormik<LanguageQualificationFormValues>({
    initialValues: languageQualificationFormValues,
    validationSchema: object({
      needEnglishTest: boolean().required(),
      resultAnnouncedIndicator: boolean().when(
        'needEnglishTest',
        ([needEnglishTest]: boolean[], schema) => {
          return needEnglishTest !== undefined && needEnglishTest
            ? schema.required()
            : schema.notRequired();
        },
      ),
      testType: string().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          return resultAnnouncedIndicator && needEnglishTest
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
      testDate: date().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          return resultAnnouncedIndicator && needEnglishTest
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
      resultReceiptDate: date().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          return resultAnnouncedIndicator && needEnglishTest
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
      listening: number().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          if (needEnglishTest && resultAnnouncedIndicator) {
            return schema.required('Required').test({
              message: 'Out of range',
              test: value => rangeValidation(value, isIELTS, 9, 30),
            });
          }
          return schema.notRequired();
        },
      ),
      reading: number().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          if (needEnglishTest && resultAnnouncedIndicator) {
            return schema.required('Required').test({
              message: 'Out of range',
              test: value => rangeValidation(value, isIELTS, 9, 30),
            });
          }
          return schema.notRequired();
        },
      ),
      speaking: number().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          if (needEnglishTest && resultAnnouncedIndicator) {
            return schema.required('Required').test({
              message: 'Out of range',
              test: value => rangeValidation(value, isIELTS, 9, 30),
            });
          }
          return schema.notRequired();
        },
      ),
      writing: number().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          if (needEnglishTest && resultAnnouncedIndicator) {
            return schema.required('Required').test({
              message: 'Out of range',
              test: value => rangeValidation(value, isIELTS, 9, 30),
            });
          }
          return schema.notRequired();
        },
      ),
      overall: number().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          if (needEnglishTest && resultAnnouncedIndicator) {
            return schema.required('Required').test({
              message: 'Out of range',
              test: value => rangeValidation(value, isIELTS, 9, 120),
            });
          }
          return schema.notRequired();
        },
      ),
      referenceNo: string().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          return needEnglishTest && resultAnnouncedIndicator
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
      expectedResultUpdateDate: date().when(
        ['resultAnnouncedIndicator', 'needEnglishTest'],
        ([resultAnnouncedIndicator, needEnglishTest]: boolean[], schema) => {
          return !resultAnnouncedIndicator && needEnglishTest
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
    }),
    onSubmit: handleSubmit,
  });

  const languagePermission = editLanguagePermission(initialValues, enableEdit);

  // results of TOEFL must be integer
  const rangeValidation = (
    value: number,
    isIELTS: boolean,
    maxIELTS: number,
    maxNonIELTS: number,
  ): boolean => {
    return isIELTS
      ? value >= 0 && value <= maxIELTS && value % 0.5 === 0
      : Number.isInteger(value) && value >= 0 && value <= maxNonIELTS;
  };

  const isIELTS =
    formik.values.testType === LANGUAGE_QUALIFICATION_TEST_TYPE.IELTS;
  const scoreRangeText = isIELTS
    ? LANGUAGE_QUALIFICATION.OPTIONS_LABEL.SCORE_RANGE.IELTS
    : LANGUAGE_QUALIFICATION.OPTIONS_LABEL.SCORE_RANGE.TOEFL;
  const scoreOverallText = isIELTS
    ? LANGUAGE_QUALIFICATION.OPTIONS_LABEL.SCORE_RANGE.IELTS
    : LANGUAGE_QUALIFICATION.OPTIONS_LABEL.SCORE_RANGE.TOEFLTOTAL;

  const handleChangeLanguageType = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    const testType = event.target.value as string;
    await formik.setValues({
      ...formik.values,
      testType,
    });
    await removeResult();
  };

  const handleChangeNeedEnglishTest = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    const needEnglishTest = event.target.value === 'false';
    await formik.setValues({
      testType: '',
      resultAnnouncedIndicator: null,
      expectedResultUpdateDate: null,
      needEnglishTest,
    });
    await removeResult();
  };

  const handleChangeResultAnnouncedIndicator = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    const resultAnnouncedIndicator = event.target.value === 'true';
    await formik.setValues({
      ...formik.values,
      testType: '',
      expectedResultUpdateDate: null,
      resultAnnouncedIndicator,
    });
    await removeResult();
  };

  const removeResult = async (): Promise<void> => {
    await formik.setFieldValue('testDate', null);
    await formik.setFieldValue('resultReceiptDate', null);
    await formik.setFieldValue('listening', '');
    await formik.setFieldValue('writing', '');
    await formik.setFieldValue('speaking', '');
    await formik.setFieldValue('reading', '');
    await formik.setFieldValue('overall', '');
    await formik.setFieldValue('referenceNo', '');
  };

  const resultsWithOrder =
    languageResultOrderMap[
      (formik.values.testType as LANGUAGE_QUALIFICATION_TEST_TYPE) ??
        LANGUAGE_QUALIFICATION_TEST_TYPE.IELTS
    ];

  const resultFields = resultsWithOrder?.map((field, index) => {
    // index === 4 is for overall
    const label = `${languageResultslabelMap[field]} (${index === 4 ? scoreOverallText : scoreRangeText})`;
    return {
      label,
      name: field,
      value: formik.values?.[field],
      disable: !(languagePermission as Record<string, boolean>)[field],
    };
  });

  function handleFilesChange(order: number, id: string): boolean {
    let newFilesArray = [...filesArray];
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD:
        newFilesArray = [
          ...newFilesArray,
          {
            id,
            name: '',
            link: '',
          },
        ];
        break;

      case FILE_MANAGE_ORDER.REMOVE:
        newFilesArray = newFilesArray.filter(doc => doc.id !== id);
        break;
      default:
        break;
    }
    setFilesArray(newFilesArray);
    return true;
  }

  const handleNavToMeetCondition = (): void => {
    window.location.href =
      '/application/' +
      applicationId +
      '?apsb=' +
      APPLICATION_MODULE.APPLICATION_PROGRESS +
      '&aphb=' +
      APPLICATION_STATUS_MODULE.CONDITIONAL_OFFER_CONFIRMATION;
  };

  const handleCancelSubmit = (): void => {
    setOpenDialog(false);
  };
  const handleOpenDialog = (): void => {
    setOpenDialog(true);
  };

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={openDialog}
        dialogTitle={''}
        dialogAction={PAGE_TEXT.GO_SUBMIT}
        cancelAction={PAGE_TEXT.BACK_TO_CHECK}
        maxWidth={'sm'}
        onDialogAction={handleNavToMeetCondition}
        onDialogClose={handleCancelSubmit}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PAGE_TEXT.QUALIFICATION.DIALOG_NOTE}
        </Typography>
      </Dialog>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        onSubmit={handleReadySubmit(formik)}
        noValidate
      >
        <div className="flex flex-row w-full justify-between">
          <div>
            <Grid container columnSpacing={4} rowSpacing={2}>
              <Grid
                size={{
                  md: 12,
                }}
              >
                <div className="text-base font-medium">
                  {LANGUAGE_QUALIFICATION.ENGLISH_UNIVERSITY + ' *'}
                </div>
                <RadioGroup
                  name="needEnglishTest"
                  value={formik.values.needEnglishTest}
                  onChange={handleChangeNeedEnglishTest}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '2rem',
                    margin: '0.5rem 0 0 0.5rem',
                  }}
                >
                  <Radio
                    value={true}
                    label={PAGE_TEXT.YES}
                    checked={formik.values.needEnglishTest === false}
                    disabled={!languagePermission.needEnglishTest}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 18,
                      },
                    }}
                    error={
                      formik.touched.needEnglishTest &&
                      Boolean(formik.errors.needEnglishTest)
                    }
                  />
                  <Radio
                    value={false}
                    label={PAGE_TEXT.NO}
                    checked={formik.values.needEnglishTest === true}
                    disabled={!languagePermission.needEnglishTest}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 18,
                      },
                    }}
                    error={
                      formik.touched.needEnglishTest &&
                      Boolean(formik.errors.needEnglishTest)
                    }
                  />
                </RadioGroup>

                {formik.values.needEnglishTest === false && (
                  <div
                    style={{
                      fontStyle: 'italic',
                      color: 'red',
                    }}
                  >
                    {
                      // Add in 30/07/2024
                      '*Please upload an official transcript/proof issued by the host institution showing the language of instruction. '
                    }
                  </div>
                )}
              </Grid>

              {formik.values.needEnglishTest && (
                <>
                  <Grid
                    size={{
                      md: 12,
                    }}
                  >
                    <div className="text-base font-medium">
                      {LANGUAGE_QUALIFICATION.HAVE_VALID_RESULT + ' *'}
                    </div>
                    <RadioGroup
                      name="resultAnnouncedIndicator"
                      value={formik.values.resultAnnouncedIndicator}
                      onChange={handleChangeResultAnnouncedIndicator}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2rem',
                        margin: '0.5rem 0 0 0.5rem',
                      }}
                    >
                      <Radio
                        value={true}
                        label={PAGE_TEXT.YES}
                        checked={
                          formik.values.resultAnnouncedIndicator === true
                        }
                        disabled={!languagePermission.resultAnnouncedIndicator}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: 18,
                          },
                        }}
                        error={
                          formik.touched.resultAnnouncedIndicator &&
                          Boolean(formik.errors.resultAnnouncedIndicator)
                        }
                      />
                      <Radio
                        value={false}
                        label={PAGE_TEXT.NO}
                        checked={
                          formik.values.resultAnnouncedIndicator === false
                        }
                        disabled={!languagePermission.resultAnnouncedIndicator}
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: 18,
                          },
                        }}
                        error={
                          formik.touched.resultAnnouncedIndicator &&
                          Boolean(formik.errors.resultAnnouncedIndicator)
                        }
                      />
                    </RadioGroup>
                  </Grid>

                  {(formik.values.resultAnnouncedIndicator === true ||
                    formik.values.resultAnnouncedIndicator === false) &&
                    (formik.values.resultAnnouncedIndicator ? (
                      <>
                        <Grid
                          size={{
                            md: 12,
                          }}
                        >
                          <Select
                            className="w-full"
                            required
                            options={languageQualificationTestTypeOptions}
                            disabled={!languagePermission.testType}
                            label={LANGUAGE_QUALIFICATION.TEST_TYPE}
                            id="language-qualification-test-type"
                            name="testType"
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.testType}
                            onChange={handleChangeLanguageType}
                            onBlur={formik.handleBlur}
                            error={
                              Boolean(formik.touched.testType) &&
                              Boolean(formik.errors.testType)
                            }
                            helperText={Boolean(
                              formik.touched.testType && formik.errors.testType,
                            )}
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6,
                          }}
                        >
                          <DatePicker
                            className="w-full"
                            disabled={!languagePermission.testDate}
                            label={LANGUAGE_QUALIFICATION.TEST_DATE}
                            required
                            id="language-qualification-test-date"
                            name="testDate"
                            maxDate={dayjs()}
                            minDate={dayjs(dayjs().subtract(2, 'year'))}
                            InputLabelProps={{ shrink: true }}
                            value={
                              formik.values.testDate !== null
                                ? dayjs(formik.values.testDate)
                                : null
                            }
                            onChange={date => {
                              void formik.setFieldValue('testDate', date);
                            }}
                            onBlur={formik.handleBlur}
                            error={
                              Boolean(formik.touched.testDate) &&
                              Boolean(formik.errors.testDate)
                            }
                            helperText={
                              Boolean(formik.touched.testDate) &&
                              formik.errors.testDate
                            }
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6,
                          }}
                        >
                          <DatePicker
                            required
                            className="w-full"
                            disabled={!languagePermission.resultReceiptDate}
                            label={LANGUAGE_QUALIFICATION.RESULT_RECEIPT_DATE}
                            id="language-qualification-result-receipt-date"
                            name="resultReceiptDate"
                            maxDate={dayjs()}
                            minDate={dayjs(formik.values.testDate)}
                            InputLabelProps={{ shrink: true }}
                            value={
                              formik.values.resultReceiptDate !== null
                                ? dayjs(formik.values.resultReceiptDate)
                                : null
                            }
                            onChange={date => {
                              void formik.setFieldValue(
                                'resultReceiptDate',
                                date,
                              );
                            }}
                            onBlur={formik.handleBlur}
                            error={
                              Boolean(formik.touched.resultReceiptDate) &&
                              Boolean(formik.errors.resultReceiptDate)
                            }
                            helperText={
                              Boolean(formik.touched.resultReceiptDate) &&
                              formik.errors.resultReceiptDate
                            }
                          />
                        </Grid>
                        {resultFields?.map((field, index) => (
                          <Grid
                            key={index}
                            size={{
                              xs: 12,
                              md: 6,
                            }}
                          >
                            <TextField
                              className="w-full"
                              disabled={field.disable}
                              label={field.label}
                              id={`language-qualification-${field.name.toString()}`}
                              name={field.name}
                              type="number"
                              InputLabelProps={{ shrink: true }}
                              required
                              value={field.value}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              inputProps={{
                                min: 0,
                                max: isIELTS ? 9 : 30,
                                step: isIELTS ? 0.5 : 1,
                              }}
                              error={
                                formik.touched[field.name] &&
                                Boolean(formik.errors[field.name])
                              }
                              helperText={
                                formik.touched[field.name] &&
                                formik.errors[field.name]
                              }
                            />
                          </Grid>
                        ))}
                        <Grid
                          size={{
                            xs: 12,
                            md: 12,
                          }}
                        >
                          <TextField
                            className="w-full"
                            label={
                              <span>
                                {isIELTS
                                  ? LANGUAGE_QUALIFICATION.TEST_REPORT_FORM_NO
                                  : LANGUAGE_QUALIFICATION.APPOINTMENT_NO}
                                <Tooltip
                                  title={
                                    <img
                                      alt="Test Image"
                                      src={
                                        isIELTS
                                          ? 'https://www.polyu.edu.hk/aradm/tpg/IELTS-Academic.png'
                                          : 'https://www.polyu.edu.hk/aradm/tpg/TOEFL-sample.png'
                                      }
                                    />
                                  }
                                  placement="top"
                                >
                                  <IconButton
                                    icon={
                                      <Help
                                        sx={{
                                          fontSize: 18,
                                          color: '#951D1D',
                                        }}
                                      />
                                    }
                                  />
                                </Tooltip>
                              </span>
                            }
                            disabled={!languagePermission.referenceNo}
                            InputLabelProps={{ shrink: true }}
                            required
                            name={'referenceNo'}
                            id="language-qualification-reference-no"
                            value={formik.values.referenceNo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.referenceNo &&
                              Boolean(formik.errors.referenceNo)
                            }
                            helperText={
                              formik.touched.referenceNo &&
                              formik.errors.referenceNo
                            }
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <div className="w-full">
                          <Grid
                            size={{
                              xs: 12,
                              md: 6,
                            }}
                          >
                            <DatePicker
                              className="w-full"
                              required
                              disabled={
                                !languagePermission.expectedResultUpdateDate
                              }
                              label={
                                LANGUAGE_QUALIFICATION.I_WILL_UPDATE_RESULT_AT
                              }
                              id="language-qualification-expected-result-update-date"
                              name="expectedResultUpdateDate"
                              minDate={dayjs()}
                              InputLabelProps={{ shrink: true }}
                              value={
                                formik.values.expectedResultUpdateDate !== null
                                  ? dayjs(
                                      formik.values.expectedResultUpdateDate,
                                    )
                                  : null
                              }
                              onChange={date => {
                                void formik.setFieldValue(
                                  'expectedResultUpdateDate',
                                  date,
                                );
                              }}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.expectedResultUpdateDate &&
                                Boolean(formik.errors.expectedResultUpdateDate)
                              }
                              helperText={
                                formik.touched.expectedResultUpdateDate &&
                                formik.errors.expectedResultUpdateDate
                              }
                            />
                          </Grid>
                        </div>
                        <div
                          style={{
                            marginLeft: '1rem',
                            fontStyle: 'italic',
                            color: 'red',
                          }}
                        >
                          {
                            // Add in 30/07/2024
                            '(Note: You must update the results before the commencement of the study.)'
                          }
                        </div>
                      </>
                    ))}
                </>
              )}
            </Grid>
          </div>
          <div style={{ paddingTop: '15px', marginLeft: '1rem' }}>
            <FilesPanel
              title={PAGE_TEXT.FILES_UPLOAD}
              disable={!languagePermission.supportingDocuments}
              disableDeleteOnly={!enableDelete}
              caption={`${PAGE_TEXT.SUPPORTING_DOCUMENTS}`}
              files={filesArray}
              handleFilesChange={handleFilesChange}
              handleLoading={handleLoading}
            />
          </div>
        </div>
        <div className="pt-12">
          {languagePermission.enableSave && (
            <Button type="submit" variant="contained">
              {PAGE_TEXT.BUTTON.SAVE}
            </Button>
          )}
          {languagePermission.enableSaveCondition && (
            <Button
              variant="contained"
              onClick={handleOpenDialog}
              sx={{ marginLeft: '2rem' }}
            >
              {PAGE_TEXT.QUALIFICATION.CONDITION_MEET}
            </Button>
          )}
        </div>
      </form>
    </Container>
  );
};

const Container = styled.div`
  margin-top: 35px;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;

  form {
    margin-bottom: 2rem;
    width: 100%;
    padding-top: 0.5rem;
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiGrid-container {
    margin: 0 0 1rem 0;
    width: calc(100%);
  }
`;
